"use client";

import type { ReactNode } from "react";
import { Badge, Button, cn } from "@wtc/ui";
import { ChevronDown, ChevronRight, FolderTree } from "lucide-react";

import type { EmailTemplateLike } from "../types";
import { hasTemplatesRecursive, type FolderWithChildren } from "../folder-tree";
import { TemplateItem } from "./TemplateItem";

export interface FolderNodeProps<T extends EmailTemplateLike> {
  folder: FolderWithChildren<T>;
  level: number;
  onPreviewTemplate: (template: T) => void;
  onApplyTemplate: (template: T) => void;
  selectedTemplateId?: string;
  expandedFolders: Set<string>;
  onToggleFolder: (folderId: string) => void;
  isFavorite: (id: string) => boolean;
  onToggleFavorite: (id: string) => void;
  wrapRow?: (template: T, row: ReactNode) => ReactNode;
}

export function FolderNode<T extends EmailTemplateLike>({
  folder,
  level,
  onPreviewTemplate,
  onApplyTemplate,
  selectedTemplateId,
  expandedFolders,
  onToggleFolder,
  isFavorite,
  onToggleFavorite,
  wrapRow,
}: FolderNodeProps<T>) {
  const isExpanded = expandedFolders.has(folder.id);
  const hasChildren = folder.children.length > 0;
  const hasMatchingContent =
    folder.templates.length > 0 || folder.children.some((child) => hasTemplatesRecursive(child));

  if (!hasMatchingContent) {
    return null;
  }

  const contentId = `template-folder-${folder.id}`;

  return (
    <div className="space-y-0.5">
      <Button
        type="button"
        variant="ghost"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={() => onToggleFolder(folder.id)}
        className={cn(
          "w-full justify-start text-left font-normal",
          "hover:bg-muted/50",
          level > 0 && "ml-4"
        )}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        <div className="flex items-center gap-2 flex-1">
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )
          ) : (
            <div className="w-4" />
          )}
          <FolderTree className="h-3 w-3 text-muted-foreground" />
          <span className="flex-1 text-xs font-medium">{folder.name}</span>
          <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
            {folder.templates.length}
          </Badge>
        </div>
      </Button>

      {isExpanded && (
        <div id={contentId} className="space-y-0.5">
          {folder.templates.map((template) => (
            <TemplateItem
              key={template.id}
              template={template}
              level={level + 1}
              selected={selectedTemplateId === template.id}
              onPreview={onPreviewTemplate}
              onApply={onApplyTemplate}
              isFavorite={isFavorite(template.id)}
              onToggleFavorite={onToggleFavorite}
              wrapRow={wrapRow}
            />
          ))}

          {folder.children.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              level={level + 1}
              onPreviewTemplate={onPreviewTemplate}
              onApplyTemplate={onApplyTemplate}
              selectedTemplateId={selectedTemplateId}
              expandedFolders={expandedFolders}
              onToggleFolder={onToggleFolder}
              isFavorite={isFavorite}
              onToggleFavorite={onToggleFavorite}
              wrapRow={wrapRow}
            />
          ))}
        </div>
      )}
    </div>
  );
}
